import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SearchIcon from '@mui/icons-material/Search';
import BarChartIcon from '@mui/icons-material/BarChart';
import { exportTableToExcel } from '../utils/excelExport';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import './MachinePartsReport.css';

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const getPartNumber = (entry) => {
  if (typeof entry === 'string') return entry;
  if (entry && typeof entry === 'object') return entry.part || '';
  return '';
};

const getPartBrand = (entry) => (entry && typeof entry === 'object' ? entry.brand || '' : '');
const getPartProcess = (entry) => (entry && typeof entry === 'object' ? entry.process || '' : '');
const getPartMachineType = (entry) => (entry && typeof entry === 'object' ? entry.machineType || '' : '');

const getPartsArrayFromMachineVal = (machineVal) => {
  if (Array.isArray(machineVal)) return machineVal;
  if (machineVal && Array.isArray(machineVal.parts)) return machineVal.parts;
  return [];
};

const partMatchesPartFilters = (entry, { partFilter, brandFilter, processFilter, machineTypeFilter }) => {
  const partNum = getPartNumber(entry);
  if (!partNum) return false;
  if (partFilter && !partNum.toLowerCase().includes(partFilter.toLowerCase())) return false;
  if (brandFilter && getPartBrand(entry) !== brandFilter) return false;
  if (processFilter && !getPartProcess(entry).toLowerCase().includes(processFilter.toLowerCase())) return false;
  if (machineTypeFilter && getPartMachineType(entry) !== machineTypeFilter) return false;
  return true;
};

const MachinePartsReport = () => {
  const tableTab0Ref = useRef(null);
  const tableTab1Ref = useRef(null);
  
  const currentDate = new Date();
  const [month, setMonth] = useState(MONTHS[currentDate.getMonth()]);
  const [year, setYear] = useState(currentDate.getFullYear().toString());
  const [data, setData] = useState({});
  const [countsData, setCountsData] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [processFilter, setProcessFilter] = useState('');
  const [machineTypeFilter, setMachineTypeFilter] = useState('');
  const [machineFilter, setMachineFilter] = useState('');
  const [partFilter, setPartFilter] = useState('');

  const [activeTab, setActiveTab] = useState(0);

  const handleExportExcel = () => {
    const activeTable = activeTab === 0 ? tableTab0Ref.current : tableTab1Ref.current;
    const reportTitle = activeTab === 0 ? 'Parts_worked_Machine_wise' : 'Number_of_parts_worked_Machine_wise';
    const filename = `${reportTitle}_${month}_${year}.xls`;
    exportTableToExcel(activeTable, filename);
  };

  // Chart dialog states
  const [selectedMachineForChart, setSelectedMachineForChart] = useState(null);
  const [chartOpen, setChartOpen] = useState(false);

  // Summary chart popup states
  const [isSummaryChartOpen, setIsSummaryChartOpen] = useState(false);
  const [dialogSearchQuery, setDialogSearchQuery] = useState('');
  const [dialogSortOrder, setDialogSortOrder] = useState('desc');

  // Parts chart popup states
  const [isPartsChartOpen, setIsPartsChartOpen] = useState(false);
  const [partsSearchQuery, setPartsSearchQuery] = useState('');
  const [partsSortOrder, setPartsSortOrder] = useState('desc');

  const handleMachineClick = (machine) => {
    setSelectedMachineForChart(machine);
    setChartOpen(true);
  };

  // Sort state: { day: number|null, dir: 'asc'|'desc' }
  const [sortConfig, setSortConfig] = useState({ day: null, dir: 'desc' });

  const handleDateSort = (day) => {
    setSortConfig(prev =>
      prev.day === day
        ? { day, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { day, dir: 'desc' }
    );
  };

  // Generate years list (e.g., from 2020 to current + 5)
  const currentYear = currentDate.getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => (currentYear - 5 + i).toString());

  const fetchData = async () => {
    setLoading(true);
    try {
      const payload = { month: month, year: year, index: "ifec" };
      const config = {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      };

      const [partsRes, countsRes] = await Promise.all([
        axios.post('https://apir4prod.bsynapse.com/ifeccustomguard/partsworkedmachinewise', payload, config),
        axios.post('https://apir4prod.bsynapse.com/ifeccustomguard/numberofpartsworkedmachinewise', payload, config)
      ]);

      setData(partsRes.data || {});
      setCountsData(countsRes.data || {});
    } catch (error) {
      console.error("Error fetching machine parts data:", error);
      setData({});
      setCountsData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [month, year]);

  // Determine the number of days in the selected month
  const monthIndex = MONTHS.indexOf(month);
  const daysInMonth = new Date(parseInt(year), monthIndex + 1, 0).getDate();
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const getFormattedDate = (d) => {
    const mm = String(monthIndex + 1).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
  };

  // Filter data
  const allMachinesSet = new Set(Object.keys(data).filter(key => key !== 'Count'));
  Object.values(countsData).forEach(dateObj => {
    if (dateObj && dateObj.machines) {
      Object.keys(dateObj.machines).forEach(machine => allMachinesSet.add(machine));
    }
  });
  const allMachines = Array.from(allMachinesSet).sort();

  // Helper to resolve machine category from both data and countsData
  const getMachineCategory = (machine) => {
    if (data[machine] && data[machine].machineCategory) {
      return data[machine].machineCategory;
    }
    for (const dateObj of Object.values(countsData)) {
      if (dateObj && dateObj.machines && dateObj.machines[machine]) {
        const mInfo = dateObj.machines[machine];
        if (mInfo && typeof mInfo === 'object' && mInfo.machineCategory) {
          return mInfo.machineCategory;
        }
      }
    }
    return '';
  };

  // Get unique machine categories
  const machineCategoriesSet = new Set();
  Object.values(data).forEach(mInfo => {
    if (mInfo && typeof mInfo === 'object' && mInfo.machineCategory) {
      machineCategoriesSet.add(mInfo.machineCategory);
    }
  });
  Object.values(countsData).forEach(dateObj => {
    if (dateObj && dateObj.machines) {
      Object.values(dateObj.machines).forEach(mInfo => {
        if (mInfo && typeof mInfo === 'object' && mInfo.machineCategory) {
          machineCategoriesSet.add(mInfo.machineCategory);
        }
      });
    }
  });
  const machineCategories = Array.from(machineCategoriesSet).sort();

  const partFilterParams = { partFilter, brandFilter, processFilter, machineTypeFilter };
  const hasPartLevelFilters = Boolean(brandFilter || processFilter || machineTypeFilter || partFilter);

  const filterPartEntries = (parts) => {
    if (!Array.isArray(parts)) return [];
    return parts.filter(p => partMatchesPartFilters(p, partFilterParams));
  };

  const machineHasMatchingParts = (machine) => {
    const machineData = data[machine] || {};
    const partsMap = machineData.parts || machineData || {};
    for (const day of daysArray) {
      const dateKey = getFormattedDate(day);
      const parts = partsMap[dateKey] || [];
      if (Array.isArray(parts) && parts.some(p => partMatchesPartFilters(p, partFilterParams))) {
        return true;
      }
    }
    for (const day of daysArray) {
      const dateKey = getFormattedDate(day);
      const dayData = countsData[dateKey] || {};
      const machineVal = (dayData.machines || {})[machine];
      const parts = getPartsArrayFromMachineVal(machineVal);
      if (parts.some(p => partMatchesPartFilters(p, partFilterParams))) {
        return true;
      }
    }
    return false;
  };

  const clearMachineIfIncompatible = (nextFilters) => {
    if (!machineFilter) return;
    if (nextFilters.categoryFilter && getMachineCategory(machineFilter) !== nextFilters.categoryFilter) {
      setMachineFilter('');
      return;
    }
    const testParams = {
      partFilter: nextFilters.partFilter ?? partFilter,
      brandFilter: nextFilters.brandFilter ?? brandFilter,
      processFilter: nextFilters.processFilter ?? processFilter,
      machineTypeFilter: nextFilters.machineTypeFilter ?? machineTypeFilter
    };
    const hasFilters = Boolean(testParams.partFilter || testParams.brandFilter || testParams.processFilter || testParams.machineTypeFilter);
    if (!hasFilters) return;

    const machineData = data[machineFilter] || {};
    const partsMap = machineData.parts || machineData || {};
    let matches = false;
    for (const day of daysArray) {
      const dateKey = getFormattedDate(day);
      const parts = partsMap[dateKey] || [];
      if (Array.isArray(parts) && parts.some(p => partMatchesPartFilters(p, testParams))) {
        matches = true;
        break;
      }
    }
    if (!matches) {
      for (const day of daysArray) {
        const dateKey = getFormattedDate(day);
        const dayData = countsData[dateKey] || {};
        const machineVal = (dayData.machines || {})[machineFilter];
        const parts = getPartsArrayFromMachineVal(machineVal);
        if (parts.some(p => partMatchesPartFilters(p, testParams))) {
          matches = true;
          break;
        }
      }
    }
    if (!matches) setMachineFilter('');
  };

  const handleCategoryChange = (e) => {
    const selectedCat = e.target.value;
    setCategoryFilter(selectedCat);
    clearMachineIfIncompatible({ categoryFilter: selectedCat });
  };

  const handleBrandChange = (e) => {
    const selected = e.target.value;
    setBrandFilter(selected);
    clearMachineIfIncompatible({ brandFilter: selected });
  };

  const handleMachineTypeChange = (e) => {
    const selected = e.target.value;
    setMachineTypeFilter(selected);
    clearMachineIfIncompatible({ machineTypeFilter: selected });
  };

  const allPartEntries = (() => {
    const entries = [];
    Object.values(data).forEach(mInfo => {
      if (mInfo && typeof mInfo === 'object') {
        const partsMap = mInfo.parts || mInfo || {};
        Object.values(partsMap).forEach(partsArr => {
          if (Array.isArray(partsArr)) entries.push(...partsArr);
        });
      }
    });
    Object.values(countsData).forEach(dateObj => {
      if (dateObj && dateObj.machines) {
        Object.values(dateObj.machines).forEach(machineVal => {
          entries.push(...getPartsArrayFromMachineVal(machineVal));
        });
      }
    });
    return entries;
  })();

  const brands = [...new Set(allPartEntries.map(getPartBrand).filter(Boolean))].sort();
  const processes = [...new Set(allPartEntries.map(getPartProcess).filter(Boolean))].sort();
  const machineTypes = [...new Set(allPartEntries.map(getPartMachineType).filter(Boolean))].sort();
  const allParts = [...new Set(allPartEntries.map(getPartNumber).filter(Boolean))].sort();

  const machinesForFilters = allMachines.filter(machine => {
    if (categoryFilter && getMachineCategory(machine) !== categoryFilter) return false;
    if (hasPartLevelFilters && !machineHasMatchingParts(machine)) return false;
    return true;
  });

  const filteredMachines = allMachines.filter(machine => {
    if (categoryFilter && getMachineCategory(machine) !== categoryFilter) return false;
    if (machineFilter && machine !== machineFilter) return false;
    if (hasPartLevelFilters && !machineHasMatchingParts(machine)) return false;
    return true;
  });

  const partsForFilters = allParts.filter(part => {
    const entryParams = { partFilter: part, brandFilter, processFilter, machineTypeFilter };

    const machinesToCheck = machineFilter
      ? [machineFilter]
      : categoryFilter
        ? allMachines.filter(m => getMachineCategory(m) === categoryFilter)
        : allMachines;

    return machinesToCheck.some(machine => {
      const machineData = data[machine] || {};
      const partsMap = machineData.parts || machineData || {};
      const dataMatch = Object.values(partsMap).some(partsArr =>
        Array.isArray(partsArr) && partsArr.some(p => partMatchesPartFilters(p, entryParams))
      );
      if (dataMatch) return true;

      return Object.values(countsData).some(dateObj => {
        if (!dateObj?.machines) return false;
        const machineVal = dateObj.machines[machine];
        return getPartsArrayFromMachineVal(machineVal).some(p => partMatchesPartFilters(p, entryParams));
      });
    });
  });

  // Sorted machine list used by both tables
  const sortedMachines = (() => {
    if (sortConfig.day === null) return filteredMachines;
    const dateKey = getFormattedDate(sortConfig.day);
    return [...filteredMachines].sort((a, b) => {
      // Tab 0: sort by number of parts in partsMap for that date
      const getCountTab0 = (machine) => {
        const machineData = data[machine] || {};
        const partsMap = machineData.parts || machineData || {};
        return filterPartEntries(partsMap[dateKey] || []).length;
      };
      // Tab 1: sort by count from countsData
      const getCountTab1 = (machine) => {
        const dayData = countsData[dateKey] || {};
        const machineMap = dayData.machines || {};
        const machineVal = machineMap[machine];
        return filterPartEntries(getPartsArrayFromMachineVal(machineVal)).length;
      };
      const getCount = activeTab === 0 ? getCountTab0 : getCountTab1;
      const diff = getCount(b) - getCount(a);
      return sortConfig.dir === 'desc' ? diff : -diff;
    });
  })();

  // Compute total parts worked for each machine in the selected month
  const getMachineMonthlyTotal = (machine) => {
    let total = 0;
    daysArray.forEach(day => {
      const dateKey = getFormattedDate(day);
      const dayData = countsData[dateKey] || {};
      const machineMap = dayData.machines || {};
      const machineVal = machineMap[machine];
      total += filterPartEntries(getPartsArrayFromMachineVal(machineVal)).length;
    });
    return total;
  };

  const getDayFilteredTotal = (day) => {
    const dateKey = getFormattedDate(day);
    const dayData = countsData[dateKey] || {};
    const machineMap = dayData.machines || {};
    return filteredMachines.reduce((sum, machine) => {
      const machineVal = machineMap[machine];
      return sum + filterPartEntries(getPartsArrayFromMachineVal(machineVal)).length;
    }, 0);
  };

  const getTab0MachineDayParts = (machine, dateKey) => {
    const machineData = data[machine] || {};
    const partsMap = machineData.parts || machineData || {};
    return filterPartEntries(partsMap[dateKey] || []);
  };

  const getTab0MachineRowTotalA = (machine) =>
    daysArray.reduce(
      (sum, day) => sum + getTab0MachineDayParts(machine, getFormattedDate(day)).length,
      0
    );

  const getTab0MachineRowTotalB = (machine) => {
    const unique = new Set();
    daysArray.forEach(day => {
      getTab0MachineDayParts(machine, getFormattedDate(day)).forEach(entry => {
        const part = getPartNumber(entry);
        if (part) unique.add(part);
      });
    });
    return unique.size;
  };

  const getTab0DayColumnTotalA = (day) => {
    const dateKey = getFormattedDate(day);
    return filteredMachines.reduce(
      (sum, machine) => sum + getTab0MachineDayParts(machine, dateKey).length,
      0
    );
  };

  const getTab0DayColumnTotalB = (day) => {
    const dateKey = getFormattedDate(day);
    const unique = new Set();
    filteredMachines.forEach(machine => {
      getTab0MachineDayParts(machine, dateKey).forEach(entry => {
        const part = getPartNumber(entry);
        if (part) unique.add(part);
      });
    });
    return unique.size;
  };

  const getTab0MonthlyGrandTotalA = () =>
    filteredMachines.reduce((sum, machine) => sum + getTab0MachineRowTotalA(machine), 0);

  const getTab0MonthlyGrandTotalB = () => {
    const unique = new Set();
    filteredMachines.forEach(machine => {
      daysArray.forEach(day => {
        getTab0MachineDayParts(machine, getFormattedDate(day)).forEach(entry => {
          const part = getPartNumber(entry);
          if (part) unique.add(part);
        });
      });
    });
    return unique.size;
  };

  // Compile, filter, and sort chart data for the popup Dialog (inherits main page filters)
  const rawChartData = filteredMachines.map(machine => ({
    machine,
    category: getMachineCategory(machine),
    totalParts: getMachineMonthlyTotal(machine)
  }));

  const filteredChartData = rawChartData.filter(item => 
    item.machine.toLowerCase().includes(dialogSearchQuery.toLowerCase())
  );

  const sortedChartData = [...filteredChartData].sort((a, b) => {
    const diff = b.totalParts - a.totalParts;
    return dialogSortOrder === 'desc' ? diff : -diff;
  });

  // Compile unique part frequencies for the active month (inherits active page filters)
  const partCountsMap = (() => {
    const counts = {};
    filteredMachines.forEach(machine => {
      const machineData = data[machine] || {};
      const partsMap = machineData.parts || machineData || {};
      daysArray.forEach(day => {
        const dateKey = getFormattedDate(day);
        const parts = filterPartEntries(partsMap[dateKey] || []);
        parts.forEach(partEntry => {
          const part = getPartNumber(partEntry);
          if (part) {
            counts[part] = (counts[part] || 0) + 1;
          }
        });
      });
    });
    return counts;
  })();

  const rawPartsChartData = Object.entries(partCountsMap).map(([part, frequency]) => ({
    part,
    frequency
  }));

  const filteredPartsChartData = rawPartsChartData.filter(item =>
    item.part.toLowerCase().includes(partsSearchQuery.toLowerCase())
  );

  const sortedPartsChartData = [...filteredPartsChartData].sort((a, b) => {
    const diff = b.frequency - a.frequency;
    return partsSortOrder === 'desc' ? diff : -diff;
  });

  const partInsights = (() => {
    if (rawPartsChartData.length === 0) return null;
    
    let totalRuns = 0;
    let maxFreq = -1;
    let minFreq = Infinity;
    let maxPart = '';

    rawPartsChartData.forEach(item => {
      totalRuns += item.frequency;
      if (item.frequency > maxFreq) {
        maxFreq = item.frequency;
        maxPart = item.part;
      }
      if (item.frequency < minFreq) {
        minFreq = item.frequency;
      }
    });

    // Collect ALL parts tied at the minimum frequency
    const minParts = rawPartsChartData
      .filter(item => item.frequency === minFreq)
      .map(item => item.part);

    const avgRuns = Math.round((totalRuns / rawPartsChartData.length) * 10) / 10;

    return {
      totalRuns,
      uniqueParts: rawPartsChartData.length,
      maxFreq,
      maxPart,
      minFreq,
      minParts,
      avgRuns
    };
  })();

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', overflow: 'auto', backgroundColor: '#f8fafc' }}>
      <Paper 
        sx={{ 
          p: 1.5, 
          px: 3,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          borderRadius: 0,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', 
          gap: 2, 
          alignItems: 'center',
          flexWrap: 'wrap',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)'
        }} 
        elevation={2}
      >
        <FormControl sx={{ minWidth: 140 }} size="small">
          <InputLabel>Month</InputLabel>
          <Select
            value={month}
            label="Month"
            onChange={(e) => setMonth(e.target.value)}
          >
            {MONTHS.map(m => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 100 }} size="small">
          <InputLabel>Year</InputLabel>
          <Select
            value={year}
            label="Year"
            onChange={(e) => setYear(e.target.value)}
          >
            {years.map(y => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          size="small"
          options={machinesForFilters}
          value={machineFilter || null}
          onChange={(event, newValue) => {
            setMachineFilter(newValue || '');
          }}
          renderInput={(params) => <TextField {...params} label="Filter Machine" />}
          sx={{ minWidth: 180 }}
        />

        <Autocomplete
          freeSolo
          size="small"
          options={partsForFilters}
          value={partFilter || ''}
          onChange={(event, newValue) => {
            const next = newValue || '';
            setPartFilter(next);
            clearMachineIfIncompatible({ partFilter: next });
          }}
          onInputChange={(event, newInputValue) => {
            const next = newInputValue || '';
            setPartFilter(next);
            clearMachineIfIncompatible({ partFilter: next });
          }}
          renderInput={(params) => <TextField {...params} label="Filter Part Number" />}
          sx={{ minWidth: 200 }}
        />

        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel id="category-filter-label">Machine Category</InputLabel>
          <Select
            labelId="category-filter-label"
            value={categoryFilter}
            label="Machine Category"
            onChange={handleCategoryChange}
          >
            <MenuItem value="">All Categories</MenuItem>
            {machineCategories.map(cat => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 140 }} size="small">
          <InputLabel id="brand-filter-label">Brand</InputLabel>
          <Select
            labelId="brand-filter-label"
            value={brandFilter}
            label="Brand"
            onChange={handleBrandChange}
          >
            <MenuItem value="">All Brands</MenuItem>
            {brands.map(brand => (
              <MenuItem key={brand} value={brand}>{brand}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          freeSolo
          size="small"
          options={processes}
          value={processFilter || ''}
          onChange={(event, newValue) => {
            const next = newValue || '';
            setProcessFilter(next);
            clearMachineIfIncompatible({ processFilter: next });
          }}
          onInputChange={(event, newInputValue) => {
            const next = newInputValue || '';
            setProcessFilter(next);
            clearMachineIfIncompatible({ processFilter: next });
          }}
          renderInput={(params) => <TextField {...params} label="Process" />}
          sx={{ minWidth: 220 }}
        />

        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel id="machine-type-filter-label">Machine Type</InputLabel>
          <Select
            labelId="machine-type-filter-label"
            value={machineTypeFilter}
            label="Machine Type"
            onChange={handleMachineTypeChange}
          >
            <MenuItem value="">All Machine Types</MenuItem>
            {machineTypes.map(type => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />
        
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)} 
          aria-label="machine parts tabs"
          sx={{ 
            minHeight: '40px',
            '& .MuiTab-root': { minHeight: '40px', py: 1, textTransform: 'none', fontWeight: 600 }
          }}
        >
          <Tab label="Parts worked Machine wise" />
          <Tab label="Number of parts worked Machine wise" />
        </Tabs>
      </Paper>

      <Box sx={{ p: 3 }}>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeTab === 0 && (
          <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', m: 0 }}>
                Parts worked Machine wise
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<BarChartIcon />}
                  onClick={() => {
                    setPartsSearchQuery('');
                    setPartsSortOrder('desc');
                    setIsPartsChartOpen(true);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#7c3aed',
                    color: '#7c3aed',
                    '&:hover': { borderColor: '#6d28d9', backgroundColor: 'rgba(124, 58, 237, 0.04)' }
                  }}
                >
                  Monthly Parts Graph
                </Button>
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportExcel}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#107c41',
                    '&:hover': { backgroundColor: '#0b592e' }
                  }}
                >
                  Export to Excel
                </Button>
              </Box>
            </Box>
            <div className="table-responsive">
              <table ref={tableTab0Ref} className="summary-table">
                <thead>
                  <tr className="thead-section-row">
                    <th rowSpan={2} className="corner-cell">
                      Machine
                    </th>
                    <th colSpan={daysInMonth} className="section-title section-title--business">
                      Month ({month})
                    </th>
                    <th colSpan={2} className="section-title section-title--business border-right-section">
                      Totals
                    </th>
                  </tr>
                  <tr className="thead-columns-row">
                    {daysArray.map(day => {
                      const isActive = sortConfig.day === day;
                      return (
                        <th
                          key={day}
                          className="col-head col-head--business"
                          style={{ minWidth: 80, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleDateSort(day)}
                          title={`Sort by day ${day}`}
                        >
                          {day}
                          <span style={{ marginLeft: 4, fontSize: '0.7em', opacity: isActive ? 1 : 0.3 }}>
                            {isActive && sortConfig.dir === 'asc' ? '▲' : '▼'}
                          </span>
                        </th>
                      );
                    })}
                    <th
                      className="col-head col-head--business"
                      style={{ minWidth: 120, textAlign: 'center' }}
                      title="Total parts worked by this machine in the month"
                    >
                      Total Parts Worked
                    </th>
                    <th
                      className="col-head col-head--business border-right-section"
                      style={{ minWidth: 120, textAlign: 'center' }}
                      title="Unique parts worked by this machine in the month"
                    >
                      Unique Parts Worked
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMachines.length === 0 ? (
                    <tr className="data-row">
                      <td colSpan={daysInMonth + 3} style={{ textAlign: 'center' }}>
                        No data available
                      </td>
                    </tr>
                  ) : (
                    <>
                    {sortedMachines.map(machine => {
                      const machineData = data[machine] || {};
                      const partsMap = machineData.parts || machineData || {};
                      const rowTotalA = getTab0MachineRowTotalA(machine);
                      const rowTotalB = getTab0MachineRowTotalB(machine);

                      return (
                        <tr key={machine} className="data-row">
                          <th 
                            scope="row" 
                            className="row-header"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleMachineClick(machine)}
                          >
                            <Box sx={{ 
                              fontWeight: 'bold', 
                              color: '#1e3a8a',
                              '&:hover': { textDecoration: 'underline', color: '#2563eb' }
                            }}>
                              {machine}
                            </Box>
                            {getMachineCategory(machine) && (
                              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 'normal' }}>
                                {getMachineCategory(machine)}
                              </Box>
                            )}
                          </th>
                          
                          {daysArray.map(day => {
                            const dateKey = getFormattedDate(day);
                            const parts = filterPartEntries(partsMap[dateKey] || []);

                            return (
                              <td key={day} className="cell-business-day" style={{ padding: '8px', textAlign: 'center' }}>
                                {parts.length > 0 ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                                    {parts.map((partEntry, idx) => (
                                      <Box key={idx} sx={{ fontSize: '0.82rem', color: '#1e293b', fontWeight: 600 }}>
                                        {getPartNumber(partEntry)}
                                      </Box>
                                    ))}
                                  </Box>
                                ) : '—'}
                              </td>
                            );
                          })}
                          <td className="cell-business-day numeric-cell" style={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {rowTotalA > 0 ? rowTotalA : '—'}
                          </td>
                          <td className="cell-business-day numeric-cell border-right-section" style={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {rowTotalB > 0 ? rowTotalB : '—'}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="data-row data-row--total">
                      <th scope="row" className="row-header" title="Total parts worked per day across all machines">
                        <strong style={{ color: '#1e3a8a' }}>Daily Total Parts Worked</strong>
                      </th>
                      {daysArray.map(day => {
                        const colTotalA = getTab0DayColumnTotalA(day);
                        return (
                          <td key={day} className="cell-business-day numeric-cell" style={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {colTotalA > 0 ? colTotalA : '—'}
                          </td>
                        );
                      })}
                      <td className="cell-business-day numeric-cell" style={{ textAlign: 'center', fontWeight: 'bold' }}>
                        {getTab0MonthlyGrandTotalA() > 0 ? getTab0MonthlyGrandTotalA() : '—'}
                      </td>
                      <td className="cell-business-day numeric-cell border-right-section" style={{ textAlign: 'center' }}>
                        —
                      </td>
                    </tr>
                    <tr className="data-row data-row--total">
                      <th scope="row" className="row-header" title="Unique parts worked per day across all machines">
                        <strong style={{ color: '#1e3a8a' }}>Daily Unique Parts Worked</strong>
                      </th>
                      {daysArray.map(day => {
                        const colTotalB = getTab0DayColumnTotalB(day);
                        return (
                          <td key={day} className="cell-business-day numeric-cell" style={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {colTotalB > 0 ? colTotalB : '—'}
                          </td>
                        );
                      })}
                      <td className="cell-business-day numeric-cell" style={{ textAlign: 'center' }}>
                        —
                      </td>
                      <td className="cell-business-day numeric-cell border-right-section" style={{ textAlign: 'center', fontWeight: 'bold' }}>
                        {getTab0MonthlyGrandTotalB() > 0 ? getTab0MonthlyGrandTotalB() : '—'}
                      </td>
                    </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </Paper>
          )}

          {activeTab === 1 && (
          <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', m: 0 }}>
                Number of parts worked Machine wise
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<BarChartIcon />}
                  onClick={() => {
                    setDialogSearchQuery('');
                    setDialogSortOrder('desc');
                    setIsSummaryChartOpen(true);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#1e3a8a',
                    color: '#1e3a8a',
                    '&:hover': { borderColor: '#2563eb', backgroundColor: 'rgba(30, 58, 138, 0.04)' }
                  }}
                >
                  Monthly Production Graph
                </Button>
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportExcel}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#107c41',
                    '&:hover': { backgroundColor: '#0b592e' }
                  }}
                >
                  Export to Excel
                </Button>
              </Box>
            </Box>
            <div className="table-responsive">
              <table ref={tableTab1Ref} className="summary-table">
                <thead>
                  <tr className="thead-section-row">
                    <th rowSpan={2} className="corner-cell">
                      Machine
                    </th>
                    <th colSpan={daysInMonth + 1} className="section-title section-title--business">
                      Month ({month})
                    </th>
                  </tr>
                  <tr className="thead-columns-row">
                    {daysArray.map(day => {
                      const isActive = sortConfig.day === day;
                      return (
                        <th
                          key={day}
                          className="col-head col-head--business"
                          style={{ minWidth: 80, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleDateSort(day)}
                          title={`Sort by day ${day}`}
                        >
                          {day}
                          <span style={{ marginLeft: 4, fontSize: '0.7em', opacity: isActive ? 1 : 0.3 }}>
                            {isActive && sortConfig.dir === 'asc' ? '▲' : '▼'}
                          </span>
                        </th>
                      );
                    })}
                    <th className="col-head col-head--business border-right-section" style={{ minWidth: 100, textAlign: 'center' }}>
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Total Row */}
                  <tr className="data-row data-row--total">
                    <th 
                      scope="row" 
                      className="row-header"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleMachineClick('TOTAL')}
                    >
                      <strong style={{ color: '#1e3a8a', textDecoration: 'underline' }}>TOTAL</strong>
                    </th>
                    {daysArray.map(day => {
                      const dayTotal = getDayFilteredTotal(day);
                      
                      return (
                        <td key={day} className="cell-business-day numeric-cell" style={{ textAlign: 'center' }}>
                          {dayTotal > 0 ? dayTotal : '—'}
                        </td>
                      );
                    })}
                    <td className="cell-business-day numeric-cell border-right-section" style={{ textAlign: 'center', fontWeight: 'bold' }}>
                      {daysArray.reduce((sum, day) => sum + getDayFilteredTotal(day), 0)}
                    </td>
                  </tr>

                  {filteredMachines.length === 0 ? (
                    <tr className="data-row">
                      <td colSpan={daysInMonth + 2} style={{ textAlign: 'center' }}>
                        No data available
                      </td>
                    </tr>
                  ) : (
                    sortedMachines.map(machine => {
                      let machineTotal = 0;
                      return (
                        <tr key={machine} className="data-row">
                          <th 
                            scope="row" 
                            className="row-header"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleMachineClick(machine)}
                          >
                            <Box sx={{ 
                              fontWeight: 'bold', 
                              color: '#1e3a8a',
                              '&:hover': { textDecoration: 'underline', color: '#2563eb' }
                            }}>
                              {machine}
                            </Box>
                            {getMachineCategory(machine) && (
                              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 'normal' }}>
                                {getMachineCategory(machine)}
                              </Box>
                            )}
                          </th>
                          
                          {daysArray.map(day => {
                            const dateKey = getFormattedDate(day);
                            const dayData = countsData[dateKey] || {};
                            const machineMap = dayData.machines || {};
                            const machineVal = machineMap[machine];
                            const count = filterPartEntries(getPartsArrayFromMachineVal(machineVal)).length;
                            machineTotal += count;

                            return (
                              <td key={day} className="cell-business-day numeric-cell" style={{ textAlign: 'center' }}>
                                {count > 0 ? count : '—'}
                              </td>
                            );
                          })}
                          <td className="cell-business-day numeric-cell border-right-section" style={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {machineTotal > 0 ? machineTotal : '—'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Paper>
          )}
        </>
      )}
      </Box>

      {/* Chart Dialog */}
      <Dialog
        open={chartOpen}
        onClose={() => setChartOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
            boxShadow: '0 12px 40px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a8a' }}>
              {selectedMachineForChart === 'TOTAL' 
                ? 'Overall Parts Trend (All Machines)' 
                : `Machine Parts Trend: ${selectedMachineForChart}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monthly Report for {month} {year}
            </Typography>
          </Box>
          <IconButton onClick={() => setChartOpen(false)} sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ width: '100%', height: 350, mt: 1 }}>
            {selectedMachineForChart && (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={daysArray.map(day => {
                    const dateKey = getFormattedDate(day);
                    if (selectedMachineForChart === 'TOTAL') {
                      return {
                        day: day,
                        dateLabel: `${day} ${month.substring(0, 3)}`,
                        total: getDayFilteredTotal(day)
                      };
                    } else {
                      const dayData = countsData[dateKey] || {};
                      const machineMap = dayData.machines || {};
                      const machineVal = machineMap[selectedMachineForChart];
                      return {
                        day: day,
                        dateLabel: `${day} ${month.substring(0, 3)}`,
                        total: filterPartEntries(getPartsArrayFromMachineVal(machineVal)).length
                      };
                    }
                  })}
                  margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="day" 
                    tickLine={false} 
                    axisLine={{ stroke: '#cbd5e1' }}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    label={{ value: 'Day of Month', position: 'insideBottom', offset: -10, fill: '#64748b', fontSize: 13, fontWeight: 600 }}
                  />
                  <YAxis 
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={{ stroke: '#cbd5e1' }}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    label={{ value: 'Total Parts Worked', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 13, fontWeight: 600, offset: 10 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: '8px', 
                      border: '1px solid #e2e8f0', 
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      fontFamily: 'inherit'
                    }}
                    labelFormatter={(label) => `Day ${label} (${month})`}
                    formatter={(value) => [`${value} parts`, 'Total Parts worked']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="#3b82f6" 
                    strokeWidth={3} 
                    activeDot={{ r: 8 }} 
                    dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Monthly Summary Chart Dialog */}
      <Dialog
        open={isSummaryChartOpen}
        onClose={() => setIsSummaryChartOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
            boxShadow: '0 12px 40px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a8a' }}>
              Monthly Production Summary: {month} {year}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total number of parts worked per machine
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto' }}>
            <TextField
              size="small"
              placeholder="Search Machine..."
              value={dialogSearchQuery}
              onChange={(e) => setDialogSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 220 }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={dialogSortOrder === 'desc' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
              onClick={() => setDialogSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: 150 }}
            >
              Sort: {dialogSortOrder === 'desc' ? 'Descending' : 'Ascending'}
            </Button>
            <IconButton onClick={() => setIsSummaryChartOpen(false)} sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {sortedChartData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, flexDirection: 'column', gap: 1 }}>
              <Typography color="text.secondary" variant="body1">
                No matching machines found.
              </Typography>
              {dialogSearchQuery && (
                <Button size="small" variant="text" onClick={() => setDialogSearchQuery('')}>
                  Clear search query
                </Button>
              )}
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto', width: '100%', mt: 1, pb: 1 }}>
              <Box sx={{ width: Math.max(600, sortedChartData.length * 65), height: 380 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={sortedChartData}
                    margin={{ top: 20, right: 30, left: 10, bottom: 40 }}
                  >
                    <defs>
                      <linearGradient id="colorTotalParts" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.9}/>
                        <stop offset="95%" stopColor="#1e3a8a" stopOpacity={0.6}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="machine" 
                      tickLine={false} 
                      axisLine={{ stroke: '#cbd5e1' }}
                      tick={{ fill: '#475569', fontSize: 10, fontWeight: 600 }}
                      interval={0}
                      angle={-35}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis 
                      allowDecimals={false}
                      tickLine={false}
                      axisLine={{ stroke: '#cbd5e1' }}
                      tick={{ fill: '#475569', fontSize: 11 }}
                      label={{ value: 'Total Parts Worked', angle: -90, position: 'insideLeft', fill: '#475569', fontSize: 12, fontWeight: 600, offset: -5 }}
                    />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const item = payload[0].payload;
                          return (
                            <Box sx={{
                              backgroundColor: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              p: 1.5,
                              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                            }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a8a', mb: 0.5 }}>
                                {item.machine}
                              </Typography>
                              {item.category && (
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                  Category: {item.category}
                                </Typography>
                              )}
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                Total Parts Worked: <span style={{ color: '#2563eb' }}>{item.totalParts}</span>
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar 
                      dataKey="totalParts" 
                      fill="url(#colorTotalParts)" 
                      radius={[4, 4, 0, 0]} 
                      barSize={32}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Monthly Parts Graph Dialog */}
      <Dialog
        open={isPartsChartOpen}
        onClose={() => setIsPartsChartOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
            boxShadow: '0 12px 40px rgba(0,0,0,0.14)'
          }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#6d28d9' }}>
              Monthly Parts Frequency: {month} {year}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Number of times each part was worked across all machines
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto' }}>
            <TextField
              size="small"
              placeholder="Search Part..."
              value={partsSearchQuery}
              onChange={(e) => setPartsSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 220 }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={partsSortOrder === 'desc' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
              onClick={() => setPartsSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: 150 }}
            >
              Sort: {partsSortOrder === 'desc' ? 'Descending' : 'Ascending'}
            </Button>
            <IconButton onClick={() => setIsPartsChartOpen(false)} sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {sortedPartsChartData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, flexDirection: 'column', gap: 1 }}>
              <Typography color="text.secondary" variant="body1">
                No matching parts found.
              </Typography>
              {partsSearchQuery && (
                <Button size="small" variant="text" onClick={() => setPartsSearchQuery('')}>
                  Clear search
                </Button>
              )}
            </Box>
          ) : (
            <>
              {/* Bar Chart */}
              <Box sx={{ overflowX: 'auto', width: '100%', mt: 1, pb: 1 }}>
                <Box sx={{ width: Math.max(640, sortedPartsChartData.length * 70), height: 360 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={sortedPartsChartData}
                      margin={{ top: 20, right: 30, left: 10, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorPartFreq" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.9}/>
                          <stop offset="95%" stopColor="#6d28d9" stopOpacity={0.6}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0e7ff" />
                      <XAxis
                        dataKey="part"
                        tickLine={false}
                        axisLine={{ stroke: '#ddd6fe' }}
                        tick={{ fill: '#4c1d95', fontSize: 9, fontWeight: 600 }}
                        interval={0}
                        angle={-40}
                        textAnchor="end"
                        height={55}
                      />
                      <YAxis
                        allowDecimals={false}
                        tickLine={false}
                        axisLine={{ stroke: '#ddd6fe' }}
                        tick={{ fill: '#475569', fontSize: 11 }}
                        label={{ value: 'Times Worked', angle: -90, position: 'insideLeft', fill: '#6d28d9', fontSize: 12, fontWeight: 600, offset: -5 }}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const item = payload[0].payload;
                            return (
                              <Box sx={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #ddd6fe',
                                borderRadius: '8px',
                                p: 1.5,
                                boxShadow: '0 4px 20px rgba(109,40,217,0.1)'
                              }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#6d28d9', mb: 0.5 }}>
                                  {item.part}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                  Times Worked: <span style={{ color: '#7c3aed' }}>{item.frequency}</span>
                                </Typography>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar
                        dataKey="frequency"
                        fill="url(#colorPartFreq)"
                        radius={[4, 4, 0, 0]}
                        barSize={30}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>

              {/* Key Insights */}
              {partInsights && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#4c1d95', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChartIcon fontSize="small" />
                    Key Insights
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                    {[
                      {
                        label: 'Total Times Worked',
                        value: partInsights.totalRuns,
                        subtitle: 'across all parts this month',
                        color: '#7c3aed',
                        bg: '#f5f3ff'
                      },
                      {
                        label: 'Unique Parts Worked',
                        value: partInsights.uniqueParts,
                        subtitle: 'distinct parts in production',
                        color: '#2563eb',
                        bg: '#eff6ff'
                      },
                      {
                        label: 'Most Worked Part',
                        value: partInsights.maxFreq,
                        subtitle: partInsights.maxPart,
                        color: '#059669',
                        bg: '#ecfdf5'
                      },
                      {
                        label: 'Avg. Frequency / Part',
                        value: partInsights.avgRuns,
                        subtitle: 'average times per part',
                        color: '#d97706',
                        bg: '#fffbeb'
                      },
                      {
                        label: 'Least Worked Part',
                        value: `${partInsights.minFreq} time${partInsights.minFreq !== 1 ? 's' : ''}`,
                        subtitle: partInsights.minParts.length === 1
                          ? partInsights.minParts[0]
                          : `${partInsights.minParts.length} parts tied (e.g. ${partInsights.minParts.slice(0, 2).join(', ')}${partInsights.minParts.length > 2 ? '…' : ''})`,
                        color: '#dc2626',
                        bg: '#fff1f2'
                      }
                    ].map((card, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          backgroundColor: card.bg,
                          border: `1px solid ${card.color}22`,
                          borderRadius: '12px',
                          p: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {card.label}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: card.color, lineHeight: 1 }}>
                          {card.value}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                          {card.subtitle}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MachinePartsReport;
